import axios from 'axios';
import { InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../utils/constants';
import { sanitizeErrorMessage, validateApiUrl } from '../utils/security';
import type {
  SigninRequest,
  ForgotPasswordRequest,
  AuthResponse,
  TokenVerificationResponse,
  ForgotPasswordResponse,
  ResetPasswordConfirmRequest,
  ResetPasswordConfirmResponse,
  EmailActionRequest,
  EmailActionInspectResponse,
  EmailActionApplyResponse,
  LogoutResponse,
  UserUpdateRequest,
  UserUpdateResponse,
  UserProfileResponse,
  UserSessionResponse,
  CreatePatientRequest,
  CreatePatientResponse,
  GetPatientsRequest,
  GetPatientsResponse,
  UpdatePatientRequest,
  UpdatePatientResponse,
  DeletePatientResponse,
  CreatePayerRequest,
  CreatePayerResponse,
  GetPayersRequest,
  GetPayersResponse,
  PayerUpdatePayload,
  UpdatePayerResponse,
  DeletePayerResponse,
  CreateClaimRequest,
  CreateClaimResponse,
  GetClaimsRequest,
  GetClaimsResponse,
  GetClaimByIdResponse,
  UpdateClaimRequest,
  UpdateClaimResponse,
  DeleteClaimResponse,
  UpdateClaimStatusResponse,
  GetCallsRequest,
  GetCallsResponse,
  AiCallingStatusResponse,
  ToggleAiCallingRequest,
  ToggleAiCallingResponse,
  ManualCallRequest,
  ManualCallResponse,
  DashboardOverviewResponse,
  BulkUploadClaimsResponse,
  AppealDraftListResponse,
  AppealTemplateListResponse,
  AppealDraftCreate,
  AppealDraftResponse,
  PayerRealityListResponse,
  VerificationSnapshotResponse,
  DenialPlaybookListResponse,
  DenialPlaybookResponse,
  PlaybookOutcomeCreate,
  PlaybookOutcomeResponse,
  PlaybookStatsResponse,
  DenialLookupResponse,
  PayerRulesResponse,
  AuthLookupResponse,
  LiveEligibilityRequest,
  LiveEligibilityResponse,
  EscalationEventCreate,
  EscalationEventResponse,
  EscalationEventListResponse,
  AppealDraftDetailResponse,
  AppealDraftContentUpdate,
  AppealDocumentCreate,
  AppealSubmitRequest,
  BaseResponse,
  VoicePlaygroundUrlResponse,
  VoicePlaygroundToolRequest,
  VoicePlaygroundToolResponse,
  PlaygroundPostCallAnalyzeRequest,
  PlaygroundPostCallAnalyzeResponse,
  ClaimFieldSchema,
  ClaimFieldSchemaUpdatePayload,
  WorkspaceSearchResponse,
  ClaimAvailableActionsResponse,
  CallTranscriptUrlResponse,
  ClaimDocumentListResponse,
  ClaimDocumentUploadRequest,
  ClaimDocumentUploadResponse,
  SyntheticClaimContext,
  OrgWorkflowConfig,
} from './types';

// Validate API URL in production (must use HTTPS)
if (process.env.NODE_ENV === 'production' && !validateApiUrl(API_BASE_URL)) {
  throw new Error('API_BASE_URL must use HTTPS in production');
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Increased to 30s for AWS RDS connection
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log the active base URL at startup to help debugging
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line no-console
  console.info('[api] API base URL:', API_BASE_URL);
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[api] Request:', config.method, config.url, config.params || config.data);
      // Don't log headers in production to avoid exposing tokens
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    // Log sanitized error in development only
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('[api] Response error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message: error.message,
        // Don't log full error data in case it contains sensitive info
      });
    }

    if (error.response?.status === 401) {
      // Don't redirect on auth endpoints (login, signup, etc.) - let the component handle the error
      const isAuthEndpoint = error.config?.url?.includes('/auth/');
      
      if (!isAuthEndpoint) {
        // Clear token and redirect to login on unauthorized (only for protected routes)
        sessionStorage.removeItem('authToken');
        // Keep the redirect but log it so we can trace where it happened
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[api] 401 received — redirecting to /auth/login');
        }
        window.location.href = '/auth/login';
      }
    }

    // Sanitize error before rejecting
    const sanitizedError = {
      ...error,
      message: sanitizeErrorMessage(error),
    };
    return Promise.reject(sanitizedError);
  }
);

export default api;

// --- API wrapper functions ---
export const signin = async (data: SigninRequest): Promise<AuthResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.signin, data);
  return response.data;
};

export const checkEmailVerification = async (): Promise<TokenVerificationResponse> => {
  const response = await api.get(API_ENDPOINTS.auth.checkEmailVerification);
  return response.data;
};

export const forgotPassword = async (data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.forgotPassword, data);
  return response.data;
};

export const confirmResetPassword = async (
  data: ResetPasswordConfirmRequest
): Promise<ResetPasswordConfirmResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.confirmResetPassword, data);
  return response.data;
};

export const inspectEmailAction = async (
  data: EmailActionRequest
): Promise<EmailActionInspectResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.inspectEmailAction, data);
  return response.data;
};

export const applyEmailAction = async (
  data: EmailActionRequest
): Promise<EmailActionApplyResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.applyEmailAction, data);
  return response.data;
};

export const logout = async (): Promise<LogoutResponse> => {
  const response = await api.post(API_ENDPOINTS.auth.logout);
  return response.data;
};

export const checkSession = async (): Promise<{ success: boolean; message: string; data: { valid: boolean } }> => {
  const response = await api.get(API_ENDPOINTS.users.session);
  return response.data;
};

export const fetchUserById = async (userId: number) => {
  const response = await api.get(`/users/${userId}`);
  return response.data;
};

export const updateUser = async (data: UserUpdateRequest): Promise<UserUpdateResponse> => {
  const response = await api.post(API_ENDPOINTS.users.update, data);
  return response.data;
};

export const getUserProfile = async (): Promise<UserProfileResponse> => {
  const response = await api.get(API_ENDPOINTS.users.me);
  return response.data;
};

export const getUserSession = async (): Promise<UserSessionResponse> => {
  const response = await api.get(API_ENDPOINTS.users.session);
  return response.data;
};

// Patient API functions
export const createPatient = async (data: CreatePatientRequest): Promise<CreatePatientResponse> => {
  const response = await api.post(API_ENDPOINTS.patients.create, data);
  return response.data;
};

export const getPatients = async (params?: GetPatientsRequest): Promise<GetPatientsResponse> => {
  const response = await api.get(API_ENDPOINTS.patients.list, { params });
  return response.data;
};

export const updatePatient = async (patientId: string, data: UpdatePatientRequest): Promise<UpdatePatientResponse> => {
  const response = await api.put(API_ENDPOINTS.patients.update(patientId), data);
  return response.data;
};

export const deletePatient = async (patientId: string): Promise<DeletePatientResponse> => {
  const response = await api.delete(API_ENDPOINTS.patients.delete(patientId));
  return response.data;
};

export interface BulkDeleteResponse {
  success: boolean;
  deleted: number;
  deleted_ids: string[];
  failed: Array<{ id: string; error: string }>;
}

export const bulkDeletePatients = async (
  params: { ids?: string[]; all?: boolean; search_term?: string | null }
): Promise<BulkDeleteResponse> => {
  const response = await api.post(API_ENDPOINTS.patients.bulkDelete, params);
  return response.data;
};

// Payer API functions
export const createPayer = async (data: CreatePayerRequest): Promise<CreatePayerResponse> => {
  const response = await api.post(API_ENDPOINTS.payers.create, data);
  return response.data;
};

export const getPayers = async (params?: GetPayersRequest): Promise<GetPayersResponse> => {
  const response = await api.get(API_ENDPOINTS.payers.list, { params });
  return response.data;
};

export const updatePayer = async (payerId: string, data: PayerUpdatePayload): Promise<UpdatePayerResponse> => {
  const response = await api.put(API_ENDPOINTS.payers.update(payerId), data);
  return response.data;
};

export const deletePayer = async (payerId: string): Promise<DeletePayerResponse> => {
  const response = await api.delete(API_ENDPOINTS.payers.delete(payerId));
  return response.data;
};

export const bulkDeletePayers = async (
  params: { ids?: string[]; all?: boolean; search_term?: string | null }
): Promise<BulkDeleteResponse> => {
  const response = await api.post(API_ENDPOINTS.payers.bulkDelete, params);
  return response.data;
};

// Provider API functions (migration 067)
export const createProvider = async (
  data: import('./types').CreateProviderRequest
): Promise<import('./types').ProviderResponse> => {
  const response = await api.post(API_ENDPOINTS.providers.create, data);
  return response.data;
};

export const getProviders = async (
  params?: import('./types').GetProvidersRequest
): Promise<import('./types').GetProvidersResponse> => {
  const response = await api.get(API_ENDPOINTS.providers.list, { params });
  return response.data;
};

export const getProviderById = async (
  providerId: string
): Promise<import('./types').ProviderResponse> => {
  const response = await api.get(API_ENDPOINTS.providers.detail(providerId));
  return response.data;
};

export const updateProvider = async (
  providerId: string,
  data: import('./types').UpdateProviderRequest
): Promise<import('./types').ProviderResponse> => {
  const response = await api.put(API_ENDPOINTS.providers.update(providerId), data);
  return response.data;
};

export const deleteProvider = async (
  providerId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(API_ENDPOINTS.providers.delete(providerId));
  return response.data;
};

export const bulkDeleteProviders = async (
  params: { ids?: string[]; all?: boolean; search_term?: string | null }
): Promise<BulkDeleteResponse> => {
  const response = await api.post(API_ENDPOINTS.providers.bulkDelete, params);
  return response.data;
};

export const lookupNppes = async (
  npi: string
): Promise<import('./types').NppesLookupResponse> => {
  const response = await api.get(API_ENDPOINTS.nppes.lookup(npi));
  return response.data;
};

// Claim API functions
export const createClaim = async (data: CreateClaimRequest): Promise<CreateClaimResponse> => {
  const response = await api.post(API_ENDPOINTS.claims.create, data);
  return response.data;
};

export const getClaims = async (params?: GetClaimsRequest): Promise<GetClaimsResponse> => {
  const response = await api.get(API_ENDPOINTS.claims.list, { params });
  return response.data;
};

export const getClaimById = async (claimId: string): Promise<GetClaimByIdResponse> => {
  const response = await api.get(API_ENDPOINTS.claims.detail(claimId));
  return response.data;
};

export const getClaimAvailableActions = async (claimId: string): Promise<ClaimAvailableActionsResponse> => {
  const response = await api.get(API_ENDPOINTS.claims.availableActions(claimId));
  return response.data;
};

export const getClaimDocuments = async (claimId: string): Promise<ClaimDocumentListResponse> => {
  const response = await api.get(API_ENDPOINTS.claims.documents(claimId));
  return response.data;
};

export const uploadClaimDocument = async (
  claimId: string,
  data: ClaimDocumentUploadRequest
): Promise<ClaimDocumentUploadResponse> => {
  const response = await api.post(API_ENDPOINTS.claims.documents(claimId), data);
  return response.data;
};

export const updateClaim = async (claimId: string, data: UpdateClaimRequest): Promise<UpdateClaimResponse> => {
  const response = await api.put(API_ENDPOINTS.claims.update(claimId), data);
  return response.data;
};

export const deleteClaim = async (claimId: string): Promise<DeleteClaimResponse> => {
  const response = await api.delete(API_ENDPOINTS.claims.delete(claimId));
  return response.data;
};

export const bulkDeleteClaims = async (
  params:
    | string[]
    | { all: true; search_term?: string | null; status?: string | null }
): Promise<{
  success: boolean;
  message: string;
  data: {
    deleted_count: number;
    skipped_count: number;
    error_count: number;
    deleted_ids: string[];
    skipped_ids: string[];
    errors: { id: string; error: string }[];
  };
}> => {
  const body = Array.isArray(params) ? { claim_ids: params } : params;
  const response = await api.post(API_ENDPOINTS.claims.bulkDelete, body);
  return response.data;
};

export const updateClaimStatus = async (claimId: string, status: string): Promise<UpdateClaimStatusResponse> => {
  const response = await api.patch(API_ENDPOINTS.claims.updateStatus(claimId), null, {
    params: { status }
  });
  return response.data;
};

// Twilio AI Calling API functions
export const getAiCallingStatus = async (): Promise<AiCallingStatusResponse> => {
  const response = await api.get(API_ENDPOINTS.twilio.aiCallingStatus);
  return response.data;
};

export const toggleAiCalling = async (data: ToggleAiCallingRequest): Promise<ToggleAiCallingResponse> => {
  const response = await api.patch(API_ENDPOINTS.twilio.toggleAiCalling, data);
  return response.data;
};

export const getVoicePlaygroundUrl = async (
  claimId: string,
  syntheticContext?: SyntheticClaimContext,
): Promise<VoicePlaygroundUrlResponse> => {
  if (syntheticContext) {
    const response = await api.post(API_ENDPOINTS.voice.playgroundUrl, {
      claim_id: claimId,
      synthetic_context: syntheticContext,
    });
    return response.data;
  }
  const response = await api.get(API_ENDPOINTS.voice.playgroundUrl, {
    params: { claim_id: claimId },
  });
  return response.data;
};

export const invokeVoicePlaygroundTool = async (
  payload: VoicePlaygroundToolRequest
): Promise<VoicePlaygroundToolResponse> => {
  const response = await api.post(API_ENDPOINTS.voice.playgroundTool, payload);
  return response.data;
};

export const analyzePlaygroundPostCall = async (
  payload: PlaygroundPostCallAnalyzeRequest,
): Promise<PlaygroundPostCallAnalyzeResponse> => {
  const response = await api.post(
    API_ENDPOINTS.workflow.playgroundPostCallAnalyze,
    payload,
  );
  return response.data;
};

// Org workflow config
export const getOrgWorkflowConfig = async (orgId: string): Promise<OrgWorkflowConfig> => {
  const response = await api.get(API_ENDPOINTS.workflow.orgConfig(orgId));
  return response.data;
};

export const updateOrgWorkflowConfig = async (
  orgId: string,
  data: Partial<
    Pick<
      OrgWorkflowConfig,
      | 'call_mode'
      | 'call_flow_template_id'
      | 'template_version'
      | 'node_toggles'
      | 'personality_config'
    >
  >,
): Promise<OrgWorkflowConfig> => {
  const response = await api.patch(API_ENDPOINTS.workflow.orgConfig(orgId), data);
  return response.data;
};

// (Removed in 062 cleanup) /org-config/operations and /org-config/tools
// were retired with the corresponding backend tables. Knowledge-share
// opt-in lives on the Claim Fields editor now.

// Per-org configurable claim upload field schema. Surfaced to in-org admins
// from the Claims page so they can map their CSV/XLSX headers to the canonical
// fields the calling pipeline consumes.
export const getOrgClaimFieldSchema = async (): Promise<ClaimFieldSchema> => {
  const response = await api.get(API_ENDPOINTS.orgConfig.claimFieldSchema);
  return response.data;
};

export const updateOrgClaimFieldSchema = async (
  payload: ClaimFieldSchemaUpdatePayload
): Promise<ClaimFieldSchema> => {
  const response = await api.put(API_ENDPOINTS.orgConfig.claimFieldSchema, payload);
  return response.data;
};

// Calls API functions
export const getCalls = async (params?: GetCallsRequest): Promise<GetCallsResponse> => {
  const response = await api.get(API_ENDPOINTS.calls.list, { params });
  return response.data;
};

// Manual Call API function
export const placeManualCall = async (data: ManualCallRequest): Promise<ManualCallResponse> => {
  const response = await api.post(API_ENDPOINTS.calls.manual, data);
  return response.data;
};

// Patient Call API function (scheduling / follow-up / copay recovery).
export const placePatientCall = async (
  data: import('./types').PatientCallRequest
): Promise<ManualCallResponse> => {
  const response = await api.post(API_ENDPOINTS.calls.patient, data);
  return response.data;
};

// SMS API functions (gated by 10DLC; sms_enabled tells the UI if live).
export const getSmsConfig = async (): Promise<{ success: boolean; sms_enabled: boolean }> => {
  const response = await api.get(API_ENDPOINTS.sms.config);
  return response.data;
};

export const sendSms = async (
  data: import('./types').SendSmsRequest
): Promise<{ success: boolean; message: string; data?: import('./types').SmsData }> => {
  const response = await api.post(API_ENDPOINTS.sms.send, data);
  return response.data;
};

export const getSmsMessages = async (params?: {
  page?: number;
  limit?: number;
  patient_id?: string;
}): Promise<{ success: boolean; data: import('./types').SmsData[]; sms_enabled: boolean }> => {
  const response = await api.get(API_ENDPOINTS.sms.list, { params });
  return response.data;
};

// Prior Authorization (278) API functions. The clearinghouse 278 path is
// not connected yet, so `createPriorAuth` records the request and the
// backend returns `voice_fallback` status with a message steering the
// operator to call the payer.
export const createPriorAuth = async (
  data: import('./types').PriorAuthCreateRequest
): Promise<{ success: boolean; message: string; data?: import('./types').PriorAuthData }> => {
  const response = await api.post(API_ENDPOINTS.priorAuth.create, data);
  return response.data;
};

export const listPriorAuth = async (): Promise<{
  success: boolean;
  message: string;
  data: import('./types').PriorAuthData[];
}> => {
  const response = await api.get(API_ENDPOINTS.priorAuth.list);
  return response.data;
};

// Dashboard API function
export const getDashboardOverview = async (): Promise<DashboardOverviewResponse> => {
  const response = await api.get(API_ENDPOINTS.dashboard.overview);
  return response.data;
};

export interface SidebarCounts {
  claims: number;
  calls: number;
  patients: number;
  payers: number;
  providers: number;
}

export const getSidebarCounts = async (): Promise<SidebarCounts> => {
  const response = await api.get(API_ENDPOINTS.dashboard.sidebarCounts);
  return response.data?.data ?? { claims: 0, calls: 0, patients: 0, payers: 0, providers: 0 };
};

export const searchWorkspace = async (
  query: string,
  limit: number = 6
): Promise<WorkspaceSearchResponse> => {
  const term = query.trim();
  if (!term) {
    return { claims: [], patients: [], payers: [] };
  }

  const [claimsResponse, patientsResponse, payersResponse] = await Promise.all([
    getClaims({ page: 1, limit, search_term: term, status: null }),
    getPatients({ page: 1, limit, search_term: term }),
    getPayers({ page: 1, limit, search_term: term }),
  ]);

  return {
    claims: (claimsResponse.data || []).map((claim) => ({
      id: claim.id,
      label: claim.claim_number || claim.id,
      description: `${claim.patient?.first_name || 'Unknown'} ${claim.patient?.last_name || ''} · ${
        claim.payer?.name || 'Unknown payer'
      }`,
      route: '/dashboard/claim-history',
    })),
    patients: (patientsResponse.data || []).map((patient) => ({
      id: patient.id,
      label: `${patient.first_name} ${patient.last_name}`.trim(),
      description: patient.patient_email || patient.phone,
      route: '/dashboard/patients',
    })),
    payers: (payersResponse.data || []).map((payer) => ({
      id: payer.id,
      label: payer.name,
      description: payer.payer_email || payer.phone,
      route: '/dashboard/payers',
    })),
  };
};

export const getAppealsReviewQueue = async (): Promise<AppealDraftListResponse> => {
  const response = await api.get(API_ENDPOINTS.appeals.reviewQueue);
  return response.data;
};

export const listAppealTemplates = async (): Promise<AppealTemplateListResponse> => {
  const response = await api.get(API_ENDPOINTS.appeals.templates);
  return response.data;
};

export const createAppealDraft = async (payload: AppealDraftCreate): Promise<AppealDraftResponse> => {
  const response = await api.post(API_ENDPOINTS.appeals.drafts, payload);
  return response.data;
};

export const getPayerReality = async (): Promise<PayerRealityListResponse> => {
  const response = await api.get(API_ENDPOINTS.payerReality.list);
  return response.data;
};

// Verification
export const runVerification = async (claimId: string): Promise<VerificationSnapshotResponse> => {
  const response = await api.post(API_ENDPOINTS.claims.verify(claimId));
  return response.data;
};

export const getVerificationSnapshot = async (claimId: string): Promise<VerificationSnapshotResponse> => {
  const response = await api.get(API_ENDPOINTS.claims.verification(claimId));
  return response.data;
};

// Playbooks
export const getPlaybooks = async (): Promise<DenialPlaybookListResponse> => {
  const response = await api.get(API_ENDPOINTS.playbooks.list);
  return response.data;
};

export const getPlaybookByCode = async (code: string): Promise<DenialPlaybookResponse> => {
  const response = await api.get(API_ENDPOINTS.playbooks.detail(code));
  return response.data;
};

export const getPlaybookStats = async (): Promise<PlaybookStatsResponse> => {
  const response = await api.get(API_ENDPOINTS.playbooks.stats);
  return response.data;
};

export const recordPlaybookOutcome = async (
  playbookId: string,
  data: PlaybookOutcomeCreate
): Promise<PlaybookOutcomeResponse> => {
  const response = await api.post(API_ENDPOINTS.playbooks.outcomes(playbookId), data);
  return response.data;
};

// Tools
export const lookupDenial = async (code: string): Promise<DenialLookupResponse> => {
  const response = await api.get(API_ENDPOINTS.tools.denialLookup, { params: { code } });
  return response.data;
};

export const getPayerRules = async (payerId: string): Promise<PayerRulesResponse> => {
  const response = await api.get(API_ENDPOINTS.tools.payerRules, { params: { payer_id: payerId } });
  return response.data;
};

export const lookupAuth = async (claimId: string): Promise<AuthLookupResponse> => {
  const response = await api.get(API_ENDPOINTS.tools.authLookup, { params: { claim_id: claimId } });
  return response.data;
};

export const runLiveEligibility = async (payload: LiveEligibilityRequest): Promise<LiveEligibilityResponse> => {
  const response = await api.post(API_ENDPOINTS.tools.liveEligibility, payload);
  return response.data;
};

// Escalations
export const createEscalation = async (
  callId: string,
  payload: EscalationEventCreate
): Promise<EscalationEventResponse> => {
  const response = await api.post(API_ENDPOINTS.escalations.create(callId), payload);
  return response.data;
};

export const listEscalations = async (callId: string): Promise<EscalationEventListResponse> => {
  const response = await api.get(API_ENDPOINTS.escalations.list(callId));
  return response.data;
};

// Appeals detail + documents
export const getAppealDraftDetail = async (draftId: string): Promise<AppealDraftDetailResponse> => {
  const response = await api.get(API_ENDPOINTS.appeals.detail(draftId));
  return response.data;
};

export const updateAppealDraftContent = async (
  draftId: string,
  payload: AppealDraftContentUpdate
): Promise<AppealDraftDetailResponse> => {
  const response = await api.put(API_ENDPOINTS.appeals.updateContent(draftId), payload);
  return response.data;
};

export const addAppealDocument = async (
  draftId: string,
  payload: AppealDocumentCreate
): Promise<AppealDraftDetailResponse> => {
  const response = await api.post(API_ENDPOINTS.appeals.addDocument(draftId), payload);
  return response.data;
};

export const submitAppeal = async (
  draftId: string,
  payload: AppealSubmitRequest
): Promise<AppealDraftDetailResponse> => {
  const response = await api.post(API_ENDPOINTS.appeals.submit(draftId), payload);
  return response.data;
};

// Calls
export const reprocessCall = async (callId: string): Promise<BaseResponse> => {
  const response = await api.post(API_ENDPOINTS.calls.reprocess(callId));
  return response.data;
};

export const getCallTranscriptUrl = async (callId: string): Promise<CallTranscriptUrlResponse> => {
  const response = await api.get(API_ENDPOINTS.calls.transcript(callId));
  return response.data;
};

export const endLiveCall = async (callId: string, scope: 'user' | 'admin' = 'user'): Promise<BaseResponse> => {
  const url = scope === 'admin'
    ? API_ENDPOINTS.calls.adminEnd(callId)
    : API_ENDPOINTS.calls.end(callId);
  const response = await api.post(url);
  return response.data;
};

// --- Bulk upload (claims) ---
export const downloadExampleClaimUploadFile = async (
  format: 'csv' | 'xlsx' = 'csv'
): Promise<Blob> => {
  const response = await api.get(API_ENDPOINTS.claims.bulkUploadExample, {
    params: { format },
    responseType: 'blob',
  });
  return response.data as Blob;
};

export interface BulkUploadOptions {
  /**
   * Mapping table: `{ csv_header: target_field }`. Layered on top of the
   * org's claim field schema by the backend so users can ship CSVs with
   * non-standard headers without first editing their schema.
   */
  mappingOverrides?: Record<string, string>;
  /**
   * When true, the server runs the full pipeline and rolls back the
   * transaction. Used by the import wizard's Commit step to surface
   * server-only errors before the actual submit.
   */
  dryRun?: boolean;
}

export const bulkUploadClaims = async (
  file: File,
  options: BulkUploadOptions = {}
): Promise<BulkUploadClaimsResponse> => {
  const form = new FormData();
  form.append('file', file);
  if (options.mappingOverrides && Object.keys(options.mappingOverrides).length > 0) {
    form.append('mapping_overrides', JSON.stringify(options.mappingOverrides));
  }
  if (options.dryRun) {
    form.append('dry_run', 'true');
  }

  const response = await api.post(API_ENDPOINTS.claims.bulkUpload, form, {
    // Let the browser/axios set the multipart boundary automatically
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// ---------------------------------------------------------------------------
// Stored upload batches (upload-first, map-later)
// ---------------------------------------------------------------------------

export const storeClaimUploadBatch = async (
  file: File,
  options: { hasHeaderRow?: boolean } = {},
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('has_header_row', String(options.hasHeaderRow ?? true));
  const response = await api.post(API_ENDPOINTS.claims.uploadBatches, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data as {
    success: boolean;
    message: string;
    data?: {
      batch: import('./claimUploadBatchTypes').ClaimUploadBatchDetail;
      preset_applied: boolean;
      preset_mapping?: Record<string, string> | null;
    };
  };
};

export const listClaimUploadBatches = async (status?: string) => {
  const response = await api.get(API_ENDPOINTS.claims.uploadBatches, {
    params: status ? { status } : undefined,
  });
  return response.data as {
    success: boolean;
    data: import('./claimUploadBatchTypes').ClaimUploadBatchSummary[];
  };
};

export const getClaimUploadBatch = async (batchId: string) => {
  const response = await api.get(API_ENDPOINTS.claims.uploadBatch(batchId));
  return response.data as {
    success: boolean;
    data: import('./claimUploadBatchTypes').ClaimUploadBatchDetail;
  };
};

export const deleteClaimUploadBatch = async (batchId: string) => {
  const response = await api.delete(API_ENDPOINTS.claims.uploadBatch(batchId));
  return response.data as { success: boolean; message: string };
};

export const downloadClaimUploadBatchFile = async (batchId: string): Promise<Blob> => {
  const response = await api.get(API_ENDPOINTS.claims.uploadBatchFile(batchId), {
    responseType: 'blob',
  });
  return response.data as Blob;
};

export const updateClaimUploadBatchMapping = async (
  batchId: string,
  body: {
    mapping: Record<string, string>;
    save_preset?: boolean;
    preset_name?: string;
    auto_apply?: boolean;
  },
) => {
  const response = await api.patch(API_ENDPOINTS.claims.uploadBatchMapping(batchId), body);
  return response.data;
};

export const processClaimUploadBatch = async (
  batchId: string,
  options: BulkUploadOptions & { file?: File } = {},
) => {
  const form = new FormData();
  if (options.mappingOverrides && Object.keys(options.mappingOverrides).length > 0) {
    form.append('mapping_overrides', JSON.stringify(options.mappingOverrides));
  }
  if (options.dryRun) {
    form.append('dry_run', 'true');
  }
  if (options.file) {
    form.append('file', options.file);
  }
  const response = await api.post(API_ENDPOINTS.claims.uploadBatchProcess(batchId), form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    // Large batches process synchronously server-side; allow up to 5 min.
    timeout: 300000,
  });
  return response.data as BulkUploadClaimsResponse;
};

export interface ClaimStatusLabel {
  slug: string;
  label: string;
  color: string;
  usage_count: number;
}

export const listClaimStatusLabels = async (): Promise<ClaimStatusLabel[]> => {
  const response = await api.get(API_ENDPOINTS.claims.statusLabels);
  return (response.data?.data ?? []) as ClaimStatusLabel[];
};

export const listClaimUploadMappingPresets = async (headersHash?: string) => {
  const response = await api.get(API_ENDPOINTS.claims.uploadMappingPresets, {
    params: headersHash ? { headers_hash: headersHash } : undefined,
  });
  return response.data as {
    success: boolean;
    data: import('./claimUploadBatchTypes').ClaimUploadMappingPreset[];
  };
};

export const upsertClaimUploadMappingPreset = async (body: {
  headers_hash: string;
  headers: string[];
  mapping: Record<string, string>;
  name?: string;
  auto_apply?: boolean;
}) => {
  const response = await api.put(API_ENDPOINTS.claims.uploadMappingPresets, body);
  return response.data;
};

// ---------------------------------------------------------------------------
// User-facing Live Calls dashboard (org-scoped via JWT).
//
// Mirrors the admin endpoints in `services/adminApi.ts` but locked to the
// authenticated user's organization. Same row/event shape (minus `org_name`)
// so the shared `LiveCallsSplitView` component renders both surfaces with
// no special-casing.
// ---------------------------------------------------------------------------

export interface LiveCallRow {
  call_id: string;
  org_id: string | null;
  /** Present only on admin-scoped payloads (cross-org list / WS).
   *  Joined in by the admin WS controller from the orgs table. */
  org_name?: string | null;
  twilio_call_sid: string | null;
  started_at: string | null;
  ended_at: string | null;
  ended_reason: string | null;
  duration_secs: number | null;
  agent_name: string | null;
  call_mode: string | null;
  claim_id: string | null;
  claim_number: string | null;
  patient_name: string | null;
  payer_name: string | null;
  payer_phone: string | null;
  current_node_id: string | null;
  current_node_name: string | null;
  hold_active: boolean;
  last_speaker: 'user' | 'agent' | string | null;
  last_transcript_preview: string | null;
  transcript_turn_count: number;
  tool_call_count: number;
  worker_pid: number | null;
  updated_at: string | null;
}

export interface LiveCallListResponse {
  items: LiveCallRow[];
  total: number;
  fetched_at: string;
}

export interface LiveCallEvent {
  id: number;
  call_id: string;
  event_type:
    | 'call_started'
    | 'call_ended'
    | 'transcript'
    | 'tool'
    | 'transition'
    | 'hold_enter'
    | 'hold_exit'
    | string;
  speaker: 'user' | 'agent' | string | null;
  payload: Record<string, unknown>;
  occurred_at: string | null;
}

export interface LiveCallEventsResponse {
  items: LiveCallEvent[];
  count: number;
  max_id: number;
}

export const listLiveCalls = async (params?: {
  include_ended?: boolean;
  limit?: number;
}): Promise<LiveCallListResponse> => {
  const response = await api.get('/live-calls', { params });
  return response.data;
};

export const getLiveCall = async (callId: string): Promise<LiveCallRow> => {
  const response = await api.get(`/live-calls/${callId}`);
  return response.data;
};

export const getLiveCallEvents = async (
  callId: string,
  params?: { since_id?: number; limit?: number },
): Promise<LiveCallEventsResponse> => {
  const response = await api.get(`/live-calls/${callId}/events`, { params });
  return response.data;
};
